// src/products/dataroom/utils/indexing.js

/**
 * Sorts items by folder status first, then alphabetically by name.
 * @param {object} a - The first item to compare.
 * @param {object} b - The second item to compare.
 * @returns {number} - -1 if a comes first, 1 if b comes first, 0 otherwise.
 */
const sortItems = (a, b) => {
  if (a.is_folder && !b.is_folder) return -1;
  if (!a.is_folder && b.is_folder) return 1;
  return a.name.localeCompare(b.name);
};

/**
 * Recursively generates a hierarchical index for dataroom items.
 * @param {Array<object>} items - The flat list of all items in the dataroom.
 * @param {string|null} parentId - The ID of the parent folder to start from.
 * @param {string} prefix - The prefix for the current level of indexing.
 * @param {Map<string, string>} indexMap - The map to store the generated indexes.
 */
const generateIndexes = (items, parentId, prefix, indexMap) => {
  const children = items.filter(item => item.parent_id === parentId).sort(sortItems);

  children.forEach((child, i) => {
    const newPrefix = prefix ? `${prefix}.${i + 1}` : `${i + 1}`;
    indexMap.set(child.id, newPrefix);
    if (child.is_folder) {
      generateIndexes(items, child.id, newPrefix, indexMap);
    }
  });
};

/**
 * Generates a map of item IDs to their hierarchical index strings.
 * @param {Array<object>} items - The flat list of all items in the dataroom.
 * @returns {Map<string, string>} - A map where keys are item IDs and values are the index strings.
 */
export const generateIndexMap = (items) => {
  const indexMap = new Map();
  generateIndexes(items, null, '', indexMap);
  return indexMap;
};

/**
 * Generates a downloadable text representation of the dataroom index.
 * @param {Array<object>} items - The flat list of all items in the dataroom.
 * @returns {string} - A string representing the indexed and indented file structure.
 */
export const generateDownloadableIndex = (items) => {
  const indexMap = generateIndexMap(items);
  let downloadableContent = 'Dataroom Index\n\n';

  const buildContent = (parentId, level) => {
    const children = items.filter(item => item.parent_id === parentId).sort(sortItems);
    children.forEach(child => {
      const index = indexMap.get(child.id);
      const indentation = '  '.repeat(level);
      const itemType = child.is_folder ? '' : ' (file)';
      downloadableContent += `${indentation}${index}. ${child.name}${itemType}\n`;
      if (child.is_folder) {
        buildContent(child.id, level + 1);
      }
    });
  };

  buildContent(null, 0);
  return downloadableContent;
};
